/*
 * ce-scroll-bar.js — DOM progress bar mirroring a horizontal scroller (`<ce-scroll-bar>` placed right AFTER it).
 * WHY JS: the reference shows a 4 px track + gradient thumb under its video row on every device; native
 * scrollbars cannot be styled on iOS and `::-webkit-scrollbar` is ignored by Firefox. One scroll listener
 * (passive) + one ResizeObserver per bar; hidden when nothing overflows.
 */
/* Several sections include this file; two identical <script src> tags EXECUTE twice (classic
   scripts are not deduplicated), so top-level class declarations threw "already declared".
   IIFE + guarded defines make the file idempotent. */
(() => {
  const ceDefine = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

class CeScrollBar extends HTMLElement {
  connectedCallback() {
    this.scroller = this.previousElementSibling;
    this.thumb = this.firstElementChild;
    if (!this.scroller || !this.thumb) return;
    this.update = this.update.bind(this);
    this.scroller.addEventListener('scroll', this.update, { passive: true });
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.scroller);
    this.update();
  }

  disconnectedCallback() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this.update);
    if (this.observer) this.observer.disconnect();
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
    const overflow = scrollWidth - clientWidth;
    this.hidden = overflow <= 1;
    if (this.hidden) return;
    const thumbRatio = Math.max(clientWidth / scrollWidth, 0.08);
    const travel = this.clientWidth * (1 - thumbRatio);
    this.thumb.style.width = `${thumbRatio * 100}%`;
    this.thumb.style.transform = `translateX(${(scrollLeft / overflow) * travel}px)`;
  }
}

ceDefine('ce-scroll-bar', CeScrollBar);

/*
 * <ce-scroll-dots> — one dot per direct child of the scroller placed right BEFORE it; the dot of the item nearest to
 * the scroll position is current; clicking a dot scrolls to its item. Hidden when nothing overflows.
 */
class CeScrollDots extends HTMLElement {
  connectedCallback() {
    this.scroller = this.previousElementSibling;
    if (!this.scroller) return;
    this.items = [...this.scroller.children];
    this.update = this.update.bind(this);
    this.innerHTML = '';
    this.items.forEach((item, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', String(i + 1));
      dot.addEventListener('click', () => this.scroller.scrollTo({ left: item.offsetLeft - this.scroller.offsetLeft, behavior: 'smooth' }));
      this.appendChild(dot);
    });
    this.scroller.addEventListener('scroll', this.update, { passive: true });
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.scroller);
    this.update();
  }

  disconnectedCallback() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this.update);
    if (this.observer) this.observer.disconnect();
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
    this.hidden = scrollWidth - clientWidth <= 1 || this.items.length < 2;
    if (this.hidden) return;
    const origin = this.scroller.offsetLeft;
    let nearest = 0;
    let best = Infinity;
    this.items.forEach((item, i) => {
      const d = Math.abs(item.offsetLeft - origin - scrollLeft);
      if (d < best) { best = d; nearest = i; }
    });
    if (scrollLeft + clientWidth >= scrollWidth - 2) nearest = this.items.length - 1;
    [...this.children].forEach((dot, i) => dot.setAttribute('aria-current', i === nearest ? 'true' : 'false'));
  }
}

ceDefine('ce-scroll-dots', CeScrollDots);

/*
 * <ce-scroll-progress> — a rule whose FILL follows a horizontal scroller: it starts at the share of the row that is
 * visible and reaches 100 % on the last slide (so the bar is never empty and "last card = full" holds exactly).
 * The scroller is `data-scroller="<id>"`, else the previous element sibling of this element's parent (the nav row).
 */
class CeScrollProgress extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.scroller;
    this.scroller = (id && document.getElementById(id)) || (this.parentElement && this.parentElement.previousElementSibling);
    this.fill = this.firstElementChild;
    if (!this.scroller || !this.fill) return;
    this.update = this.update.bind(this);
    this.scroller.addEventListener('scroll', this.update, { passive: true });
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.scroller);
    this.update();
  }

  disconnectedCallback() {
    if (this.scroller) this.scroller.removeEventListener('scroll', this.update);
    if (this.observer) this.observer.disconnect();
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
    const overflow = scrollWidth - clientWidth;
    if (overflow <= 1) { this.fill.style.transform = 'scaleX(1)'; return; }
    const base = Math.min(clientWidth / scrollWidth, 0.9);
    const ratio = base + (1 - base) * Math.min(Math.max(scrollLeft / overflow, 0), 1);
    this.fill.style.transform = `scaleX(${ratio})`;
    this.setAttribute('aria-valuenow', Math.round(ratio * 100));
  }
}

ceDefine('ce-scroll-progress', CeScrollProgress);
})();
